import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { TYPE_COLOR } from "./outcomes";
import { billPeriodDays, money, shortDate, UNBILLED_PERIOD_DAYS } from "./DetailParts";

const todayDate = () => moment().format("YYYY-MM-DD");
const dayOf = (value) => (value ? String(value).slice(0, 10) : null);

const EMPTY_IN = {
  unit: null,
  moveInDate: todayDate(),
  firstName: "",
  lastName: "",
  email: "",
  phone1: "",
  phone2: "",
  ssn: "",
  billDelivery: "email",
  meterRead: "",
  readAmtType: "ACT",
  otherReads: {},
  billingAddressType: "unit",
  billingAddress1: "",
  billingAddress2: "",
  billingCity: "",
  billingState: "",
  billingZip: "",
  leaseEndDate: "",
  alsoMoveOutOccupant: true,
  occupantMoveOutDate: "",
  notify: true,
};

const EMPTY_OUT = {
  account: null,
  moveOutDate: todayDate(),
  meterRead: "",
  readAmtType: "ACT",
  otherReads: {},
  email: "",
  phone: "",
  fwdAddress1: "",
  fwdAddress2: "",
  fwdCity: "",
  fwdState: "",
  fwdZip: "",
  notify: true,
};

export function useSearch(path, query) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setOptions([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      axios.get(path, { params: { q } })
        .then((response) => { if (active) setOptions(Array.isArray(response.data) ? response.data : []); })
        .catch(() => { if (active) setOptions([]); })
        .finally(() => { if (active) setLoading(false); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [path, query]);
  return { options, loading };
}

function useMoveContext(params) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const key = params ? JSON.stringify(params) : "";
  useEffect(() => {
    if (!params) {
      setContext(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    axios.get("/moves-dashboard/new-move-context", { params })
      .then((response) => { if (active) setContext(response.data); })
      .catch(() => { if (active) setContext(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [key]);
  return { context, loading };
}

// The same rule the batch applies: a final bill needs the move date to be after the meter's last read.
function meterHint(reads, moveDate) {
  if (!reads || !reads.length) return { tone: "warn", text: "This unit's meter has no reads, so the move runs with no final bill." };
  if (!moveDate) return null;
  const latest = reads[0];
  const lastDay = dayOf(latest.readAt);
  if (moveDate <= lastDay) {
    return Number(latest.billed) === 1
      ? { tone: "info", text: `The meter was read and billed on ${shortDate(lastDay)}, on or after this date, so there's no final bill.` }
      : { tone: "bad", text: `The ${shortDate(lastDay)} read is on or after this date and isn't billed yet. The batch flags the move until it is.` };
  }
  const lastBilled = reads.find((r) => Number(r.billed) === 1);
  if (!lastBilled) return { tone: "info", text: `Last read ${shortDate(lastDay)} (${Number(latest.readAmount).toLocaleString()}) isn't billed yet.` };
  const days = billPeriodDays(dayOf(lastBilled.readAt), moveDate);
  if (days <= UNBILLED_PERIOD_DAYS) {
    return { tone: "info", text: `The meter was billed through ${shortDate(lastBilled.readAt)}, a day or less before this date, so there's no final bill.` };
  }
  return { tone: "ok", text: `Final bill period ${shortDate(lastBilled.readAt)} → ${shortDate(moveDate)}, ${days} days, from the ${Number(lastBilled.readAmount).toLocaleString()} read.` };
}

const TONES = {
  ok: { color: "#2e7d32", Icon: CheckCircleIcon },
  info: { color: "#5f6670", Icon: InfoOutlinedIcon },
  warn: { color: "#9a6400", Icon: WarningAmberIcon },
  bad: { color: "#c62828", Icon: ErrorIcon },
};

function Hint({ tone, children, action }) {
  const { color, Icon } = TONES[tone];
  return (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", py: 0.6 }}>
      <Icon sx={{ fontSize: 17, color, mt: "1px" }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: tone === "bad" ? color : "text.primary" }}>{children}</Typography>
        {action}
      </Box>
    </Box>
  );
}

function SideHeading({ children }) {
  return (
    <Typography sx={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", mb: 0.5 }}>
      {children}
    </Typography>
  );
}

function openLink(kind, uuid, label = "Open it") {
  return (
    <Link component={RouterLink} to={`/move-ins-outs/${kind}/${uuid}`} target="_blank" sx={{ fontSize: 12.5 }}>
      {label} ↗
    </Link>
  );
}

function MoveInContext({ form, setForm, context, loading }) {
  if (!form.unit) return <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Pick a unit to see who's in it, what's already filed, and whether a final bill is possible.</Typography>;
  if (loading && !context) return <CircularProgress size={20} />;
  if (!context || !context.unit) return null;
  const { unit, occupant, occupantMoveOuts, openMoveIns, reads, lastBill } = context;
  const email = form.email.trim().toLowerCase();
  const tenant = occupant && occupant.customerType === "Tenant";
  const tenantMoveOut = occupantMoveOuts && occupantMoveOuts[0];
  return (
    <Box>
      <SideHeading>The unit</SideHeading>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{unit.unitAddress}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1 }}>{unit.propertyName}{unit.pmName ? ` · ${unit.pmName}` : ""}</Typography>

      {!occupant ? (
        <Hint tone="warn">No current occupant on record.</Hint>
      ) : tenant ? (
        tenantMoveOut ? (
          tenantMoveOut.moveDate > form.moveInDate
            ? <Hint tone="bad" action={openLink("out", tenantMoveOut.uuid)}>{occupant.name}'s move-out ({shortDate(tenantMoveOut.moveDate)}) is after this move-in, so the batch flags the move-in.</Hint>
            : <Hint tone="ok" action={openLink("out", tenantMoveOut.uuid)}>{occupant.name} (tenant) moves out {shortDate(tenantMoveOut.moveDate)}, before this move-in.</Hint>
        ) : (
          <Hint
            tone={form.alsoMoveOutOccupant ? "info" : "bad"}
            action={(
              <Box sx={{ mt: 0.25 }}>
                <FormControlLabel
                  sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
                  control={<Checkbox size="small" checked={form.alsoMoveOutOccupant} onChange={(e) => setForm({ ...form, alsoMoveOutOccupant: e.target.checked })} />}
                  label="Also file their move-out"
                />
                {form.alsoMoveOutOccupant ? (
                  <TextField
                    size="small"
                    type="date"
                    label="Their move-out date"
                    value={form.occupantMoveOutDate || form.moveInDate}
                    onChange={(e) => setForm({ ...form, occupantMoveOutDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mt: 0.5, width: 180 }}
                  />
                ) : null}
              </Box>
            )}
          >
            {occupant.name} (tenant, #{occupant.accountNum}) still occupies the unit with no move-out filed.
            {form.alsoMoveOutOccupant ? "" : " The batch flags this move-in until there is one."}
          </Hint>
        )
      ) : (
        <Hint tone="ok">
          {occupant.customerType === "Management Co" ? "The PM holds the unit" : `${occupant.customerType || "An occupant"} holds the unit`}: {occupant.name} (#{occupant.accountNum}) moves out and gets the final bill.
        </Hint>
      )}

      {(openMoveIns || []).map((m) => {
        const same = email && (m.email || "").trim().toLowerCase() === email;
        return (
          <Hint key={m.uuid} tone={same ? "bad" : "warn"} action={openLink("in", m.uuid)}>
            {same
              ? `${m.name} already has an open move-in here (${shortDate(m.moveDate)}). A second one is a duplicate.`
              : `${m.name} already has an open move-in here (${shortDate(m.moveDate)}). The batch keeps only one open move-in per unit.`}
          </Hint>
        );
      })}

      {(() => {
        const hint = meterHint(reads, form.moveInDate);
        return hint ? <Hint tone={hint.tone}>{hint.text}</Hint> : null;
      })()}
      {lastBill ? <Hint tone="info">Last bill on this unit: {money(lastBill.total)} on {shortDate(lastBill.billDate)}.</Hint> : null}
    </Box>
  );
}

function MoveOutContext({ form, context, loading }) {
  if (!form.account) return <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Pick the account to see its unit, anything already filed, and whether a final bill is possible.</Typography>;
  if (loading && !context) return <CircularProgress size={20} />;
  if (!context || !context.account) return null;
  const { account, accountMoveOuts, unit, openMoveIns, reads, lastBill, unitPm } = context;
  const existing = accountMoveOuts && accountMoveOuts[0];
  return (
    <Box>
      <SideHeading>The account</SideHeading>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{account.name || "—"} · #{account.accountNum}</Typography>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1 }}>
        {[account.customerType, account.status].filter(Boolean).join(" · ")}
        {unit ? ` · ${unit.unitAddress}, ${unit.propertyName}` : ""}
      </Typography>

      {existing ? <Hint tone="bad" action={openLink("out", existing.uuid)}>This account already has an open move-out ({shortDate(existing.moveDate)}).</Hint> : null}
      {account.customerType && account.customerType !== "Tenant" ? (
        <Hint tone="warn">This is a {account.customerType} account occupying {account.unitsOccupied} unit{Number(account.unitsOccupied) === 1 ? "" : "s"}. Moving it out closes it.</Hint>
      ) : null}
      {Number(account.unitsOccupied) === 0 ? <Hint tone="warn">This account isn't the current occupant of any unit.</Hint> : null}
      {account.status && account.status !== "Active" ? <Hint tone="info">Account status: {account.status}.</Hint> : null}

      {(openMoveIns || []).map((m) => (
        form.moveOutDate > m.moveDate
          ? <Hint key={m.uuid} tone="warn" action={openLink("in", m.uuid)}>{m.name} moves in {shortDate(m.moveDate)}, before this move-out, so their move-in stays blocked until this runs.</Hint>
          : <Hint key={m.uuid} tone="ok" action={openLink("in", m.uuid)}>{m.name} moves in {shortDate(m.moveDate)}, after this move-out.</Hint>
      ))}

      {unit ? (() => {
        const hint = meterHint(reads, form.moveOutDate);
        return hint ? <Hint tone={hint.tone}>{hint.text}</Hint> : null;
      })() : null}
      {unitPm && account && account.customerType === "Tenant" ? (
        unitPm.handoffFails ? (
          <Hint tone="warn">
            Processing can't move the PM in: {unitPm.handoffProblem}. The tenant stays on the unit, and a move-in after it gets flagged.
          </Hint>
        ) : (
          <Hint tone="info">
            When it's processed, the PM moves in to keep the unit billing: {unitPm.name}{unitPm.accountNum ? ` (#${unitPm.accountNum})` : " (on a new PM account)"}.
          </Hint>
        )
      ) : null}
      {lastBill ? <Hint tone="info">Last bill on this unit: {money(lastBill.total)} on {shortDate(lastBill.billDate)}.</Hint> : null}
    </Box>
  );
}

// Garage reads typed with a new move, as the API takes them.
const otherMeterReadsOf = (otherReads) => Object.entries(otherReads || {})
  .filter(([, value]) => String(value).trim() !== "")
  .map(([meterId, readAmount]) => ({ meterId, readAmount }));

function Row({ children, columns = 2 }) {
  return <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, minmax(0, 1fr))` }, gap: 1.5 }}>{children}</Box>;
}

export default function NewMoveDialog({ open, initialKind = "in", onClose }) {
  const navigate = useNavigate();
  const [kind, setKind] = useState(initialKind);
  const [inForm, setInForm] = useState(EMPTY_IN);
  const [outForm, setOutForm] = useState(EMPTY_OUT);
  const [unitQuery, setUnitQuery] = useState("");
  const [accountQuery, setAccountQuery] = useState("");
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind(initialKind);
    setInForm({ ...EMPTY_IN, moveInDate: todayDate() });
    setOutForm({ ...EMPTY_OUT, moveOutDate: todayDate() });
    setErrors({});
    setFailure(null);
  }, [open, initialKind]);

  const units = useSearch("/moves-dashboard/unit-search", unitQuery);
  const accounts = useSearch("/moves-dashboard/account-search", accountQuery);
  const inContext = useMoveContext(inForm.unit ? { unitUuid: inForm.unit.unitUuid } : null);
  const outContext = useMoveContext(outForm.account ? { accountId: outForm.account.accountId } : null);

  const field = (form, setForm, name, label, props = {}) => (
    <TextField
      size="small"
      label={label}
      value={form[name]}
      onChange={(event) => { setForm({ ...form, [name]: event.target.value }); setErrors((e) => ({ ...e, [name]: undefined })); }}
      error={Boolean(errors[name])}
      helperText={errors[name] || props.helperText}
      fullWidth
      {...props}
    />
  );

  const submit = async (allowDuplicate = false) => {
    setSaving(true);
    setFailure(null);
    try {
      if (kind === "in") {
        const { unit, otherReads, ...rest } = inForm;
        const occupant = inContext.context && inContext.context.occupant;
        const needsMoveOut = occupant && occupant.customerType === "Tenant" && !(inContext.context.occupantMoveOuts || []).length;
        const response = await axios.post("/moves-dashboard/move-ins", {
          ...rest,
          unitUuid: unit && unit.unitUuid,
          alsoMoveOutOccupant: Boolean(needsMoveOut && inForm.alsoMoveOutOccupant),
          occupantMoveOutDate: inForm.occupantMoveOutDate || inForm.moveInDate,
          otherMeterReads: otherMeterReadsOf(otherReads),
          allowDuplicate,
        });
        onClose();
        navigate(`/move-ins-outs/in/${response.data.uuid}`, { state: { notice: createdNotice("Move-in", response.data, occupant) } });
      } else {
        const { account, otherReads, ...rest } = outForm;
        const response = await axios.post("/moves-dashboard/move-outs", {
          ...rest,
          accountId: account && account.accountId,
          otherMeterReads: otherMeterReadsOf(otherReads),
          allowDuplicate,
        });
        onClose();
        navigate(`/move-ins-outs/out/${response.data.uuid}`, { state: { notice: createdNotice("Move-out", response.data) } });
      }
    } catch (err) {
      const data = err?.response?.data || {};
      setErrors(data.errors || {});
      setFailure({ message: data.message || "The move couldn't be created.", existingUuid: data.existingUuid, duplicate: err?.response?.status === 409 });
    } finally {
      setSaving(false);
    }
  };

  const readType = (form, setForm) => (Number(form.meterRead) > 0 ? (
    <TextField size="small" select label="Read type" value={form.readAmtType} onChange={(e) => setForm({ ...form, readAmtType: e.target.value })} fullWidth>
      <MenuItem value="ACT">Actual</MenuItem>
      <MenuItem value="EST">Estimate</MenuItem>
    </TextField>
  ) : null);
  // Most units have one meter, so the garage field only shows on a unit that has a second one.
  const otherMeterFields = (form, setForm, context) => ((context && context.unitMeters) || []).slice(1).map((m) => (
    <TextField
      key={m.meterId}
      size="small"
      type="number"
      label={`${Number(m.meterCode) === 2 ? "Garage" : "Second"} meter #${m.meterNum} read (optional)`}
      value={(form.otherReads || {})[m.meterId] ?? ""}
      onChange={(e) => setForm({ ...form, otherReads: { ...(form.otherReads || {}), [m.meterId]: e.target.value } })}
      inputProps={{ min: 0 }}
      helperText="Leave blank to estimate"
      fullWidth
    />
  ));

  const unitOptionLabel = (u) => `${u.unitAddress || "—"} · ${u.propertyName}`;
  const accountOptionLabel = (a) => `${a.name || "—"} · #${a.accountNum}`;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2, pb: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>New move</Typography>
        <ToggleButtonGroup size="small" exclusive value={kind} onChange={(e, next) => { if (next) { setKind(next); setErrors({}); setFailure(null); } }}>
          <ToggleButton value="in" sx={{ textTransform: "none", px: 2, "&.Mui-selected": { color: TYPE_COLOR.in } }}>Move-in</ToggleButton>
          <ToggleButton value="out" sx={{ textTransform: "none", px: 2, "&.Mui-selected": { color: TYPE_COLOR.out } }}>Move-out</ToggleButton>
        </ToggleButtonGroup>
        <IconButton aria-label="Close" onClick={onClose} disabled={saving} sx={{ ml: "auto" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 300px" }, gap: 3 }}>
          {kind === "in" ? (
            <Box sx={{ display: "grid", gap: 1.5, alignContent: "start" }}>
              <Autocomplete
                size="small"
                options={units.options}
                loading={units.loading}
                value={inForm.unit}
                filterOptions={(x) => x}
                isOptionEqualToValue={(a, b) => a.unitUuid === b.unitUuid}
                getOptionLabel={unitOptionLabel}
                onInputChange={(e, value, reason) => { if (reason === "input") setUnitQuery(value); }}
                onChange={(e, value) => { setInForm({ ...inForm, unit: value }); setErrors((x) => ({ ...x, unitUuid: undefined })); }}
                noOptionsText={unitQuery.trim().length < 2 ? "Type an address, property, or occupant" : "No units match"}
                renderOption={(props, u) => (
                  <li {...props} key={u.unitUuid}>
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{u.unitAddress}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {u.propertyName}{u.occupantName ? ` · ${u.occupantName} (${u.occupantCustomerType || "occupant"})` : " · no occupant"}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} autoFocus label="Unit" placeholder="Address, property, or current occupant" error={Boolean(errors.unitUuid)} helperText={errors.unitUuid} />
                )}
              />
              <Row>
                {field(inForm, setInForm, "moveInDate", "Move-in date", { type: "date", InputLabelProps: { shrink: true } })}
                {field(inForm, setInForm, "meterRead", (inContext.context?.unitMeters || []).length > 1 ? "Main meter read (optional)" : "Meter read (optional)", { type: "number", inputProps: { min: 0 }, helperText: "Leave blank to estimate" })}
              </Row>
              {readType(inForm, setInForm) || (inContext.context?.unitMeters || []).length > 1 ? (
                <Row>
                  {readType(inForm, setInForm)}
                  {otherMeterFields(inForm, setInForm, inContext.context)}
                </Row>
              ) : null}
              <Row>
                {field(inForm, setInForm, "firstName", "First name")}
                {field(inForm, setInForm, "lastName", "Last name")}
              </Row>
              <Row>
                {field(inForm, setInForm, "email", "Email", { type: "email" })}
                {field(inForm, setInForm, "phone1", "Phone (optional)")}
              </Row>
              <Row>
                {field(inForm, setInForm, "phone2", "Second phone (optional)")}
                {field(inForm, setInForm, "ssn", "SSN (optional)", { inputProps: { maxLength: 11, autoComplete: "off" }, helperText: errors.ssn || "Only the last 4 show after it's saved" })}
              </Row>
              <Row>
                <TextField size="small" select label="Bills sent by" value={inForm.billDelivery} onChange={(e) => setInForm({ ...inForm, billDelivery: e.target.value })} fullWidth>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="paper">Paper (mailed)</MenuItem>
                  <MenuItem value="both">Email and paper</MenuItem>
                </TextField>
              </Row>
              <Box>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Billing address</Typography>
                <RadioGroup row value={inForm.billingAddressType} onChange={(e) => setInForm({ ...inForm, billingAddressType: e.target.value })}>
                  <FormControlLabel value="unit" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>The unit</Typography>} />
                  <FormControlLabel value="other" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>A different address</Typography>} />
                </RadioGroup>
              </Box>
              {inForm.billingAddressType === "other" ? (
                <>
                  <Row>
                    {field(inForm, setInForm, "billingAddress1", "Address")}
                    {field(inForm, setInForm, "billingAddress2", "Apt / suite (optional)")}
                  </Row>
                  <Row columns={3}>
                    {field(inForm, setInForm, "billingCity", "City")}
                    {field(inForm, setInForm, "billingState", "State")}
                    {field(inForm, setInForm, "billingZip", "ZIP")}
                  </Row>
                </>
              ) : null}
              <Row>
                {field(inForm, setInForm, "leaseEndDate", "Lease end (optional)", { type: "date", InputLabelProps: { shrink: true } })}
              </Row>
              <FormControlLabel
                control={<Checkbox size="small" checked={inForm.notify} onChange={(e) => setInForm({ ...inForm, notify: e.target.checked })} />}
                label={<Typography sx={{ fontSize: 13 }}>Email the customer and property manager that the move-in was received</Typography>}
              />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 1.5, alignContent: "start" }}>
              <Autocomplete
                size="small"
                options={accounts.options}
                loading={accounts.loading}
                value={outForm.account}
                filterOptions={(x) => x}
                isOptionEqualToValue={(a, b) => a.accountId === b.accountId}
                getOptionLabel={accountOptionLabel}
                onInputChange={(e, value, reason) => { if (reason === "input") setAccountQuery(value); }}
                onChange={(e, value) => {
                  setOutForm({ ...outForm, account: value, email: value ? value.email || "" : "", phone: value ? value.phone || "" : "" });
                  setErrors((x) => ({ ...x, accountId: undefined }));
                }}
                noOptionsText={accountQuery.trim().length < 2 ? "Type a name, account #, email, or address" : "No accounts match"}
                renderOption={(props, a) => (
                  <li {...props} key={a.accountId}>
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{a.name || "—"} · #{a.accountNum}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {[a.customerType, a.status].filter(Boolean).join(" · ")}
                        {a.unitAddress ? ` · ${a.unitAddress}, ${a.propertyName}` : " · no unit"}
                        {a.unitAddress && Number(a.currentOccupant) !== 1 ? " (former)" : ""}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} autoFocus label="Account" placeholder="Name, account #, email, or address" error={Boolean(errors.accountId)} helperText={errors.accountId} />
                )}
              />
              <Row>
                {field(outForm, setOutForm, "moveOutDate", "Move-out date", { type: "date", InputLabelProps: { shrink: true } })}
                {field(outForm, setOutForm, "meterRead", (outContext.context?.unitMeters || []).length > 1 ? "Main meter read (optional)" : "Meter read (optional)", { type: "number", inputProps: { min: 0 }, helperText: "Leave blank to estimate" })}
              </Row>
              {readType(outForm, setOutForm) || (outContext.context?.unitMeters || []).length > 1 ? (
                <Row>
                  {readType(outForm, setOutForm)}
                  {otherMeterFields(outForm, setOutForm, outContext.context)}
                </Row>
              ) : null}
              <Row>
                {field(outForm, setOutForm, "email", "Email")}
                {field(outForm, setOutForm, "phone", "Phone")}
              </Row>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>Forwarding address (optional)</Typography>
              <Row>
                {field(outForm, setOutForm, "fwdAddress1", "Address")}
                {field(outForm, setOutForm, "fwdAddress2", "Apt / suite")}
              </Row>
              <Row columns={3}>
                {field(outForm, setOutForm, "fwdCity", "City")}
                {field(outForm, setOutForm, "fwdState", "State")}
                {field(outForm, setOutForm, "fwdZip", "ZIP")}
              </Row>
              <FormControlLabel
                control={<Checkbox size="small" checked={outForm.notify} onChange={(e) => setOutForm({ ...outForm, notify: e.target.checked })} />}
                label={<Typography sx={{ fontSize: 13 }}>Email the customer and property manager that the move-out was received</Typography>}
              />
            </Box>
          )}

          <Box sx={{ bgcolor: "#f7f8fa", borderRadius: 1, p: 1.75, alignSelf: "start", position: { md: "sticky" }, top: 0 }}>
            {kind === "in"
              ? <MoveInContext form={inForm} setForm={setInForm} context={inContext.context} loading={inContext.loading} />
              : <MoveOutContext form={outForm} context={outContext.context} loading={outContext.loading} />}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        {failure ? (
          <Alert
            severity={failure.duplicate ? "warning" : "error"}
            sx={{ mr: "auto", py: 0, flex: 1 }}
            action={failure.duplicate ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {failure.existingUuid ? openLink(kind, failure.existingUuid, "Open existing") : null}
                <Button size="small" color="inherit" onClick={() => submit(true)} disabled={saving}>Create anyway</Button>
              </Box>
            ) : null}
          >
            {failure.message}
          </Alert>
        ) : null}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={() => submit(false)} disabled={saving} sx={{ textTransform: "none", bgcolor: TYPE_COLOR[kind], "&:hover": { bgcolor: TYPE_COLOR[kind], filter: "brightness(0.9)" } }}>
          {saving ? "Creating…" : kind === "in" ? "Create move-in" : "Create move-out"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function createdNotice(label, result, occupant) {
  const parts = [`${label} created.`];
  if (result.moveOutUuid) parts.push(`Also filed ${occupant && occupant.name ? `${occupant.name}'s` : "the tenant's"} move-out.`);
  if (result.linkedToPortalLogin) parts.push("The email already has a portal login, so the new account will be linked to it.");
  (result.warnings || []).forEach((w) => parts.push(w));
  const sent = result.notifications ? Object.entries(result.notifications) : [];
  const failed = sent.filter(([, status]) => status !== "sent").map(([who, status]) => `${who === "propertyManager" ? "property manager" : who} ${status}`);
  if (failed.length) parts.push(`Emails: ${failed.join(", ")}.`);
  return { message: parts.join(" "), severity: failed.length || (result.warnings || []).length ? "warning" : "success" };
}
